// Small pieces built from the design system, used across pages.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Btn, PersonAvatar, Tag, Tooltip, brandMark } from '../ds/index.js'
import { Icon, EXTRA_ICONS } from './icons'
import { prefersReducedMotion } from '../lib/prefs'
import { embedded } from '../lib/embed'
import type { Floater } from '../lib/room'
import { ROOM_REACTIONS } from '@shared/rules'

/** DS Btn, but any icon name works (DS set or ours). */
export function XBtn({ icon, children, ...rest }: { icon?: string; children?: ReactNode; [k: string]: unknown }) {
  if (!icon || !EXTRA_ICONS.includes(icon)) return <Btn icon={icon} {...rest}>{children}</Btn>
  const sm = rest.size === 'sm'
  return (
    <Btn {...rest}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Icon name={icon} size={sm ? 14 : 16} stroke={2.4} />
        {children}
      </span>
    </Btn>
  )
}

/** A person's initial, or a team member's photo when there is one. */
export function Avatar({ name, picture, size = 28 }: { name: string; picture?: string | null; size?: number }) {
  const [broken, setBroken] = useState(false)
  if (!picture || broken) return <PersonAvatar name={name} size={size} />
  return <img src={picture} alt="" title={name} referrerPolicy="no-referrer" onError={() => setBroken(true)}
    style={{ width: size, height: size, flex: 'none', borderRadius: '50%', border: '1.5px solid var(--ink)', objectFit: 'cover', boxSizing: 'border-box', background: 'var(--inset)' }} />
}

/** DS IconBtn look, any icon. */
export function XIconBtn({ icon, label, onClick, size = 'md', framed, active, danger, disabled, style }: {
  icon: string; label: string; onClick?: () => void; size?: 'sm' | 'md'; framed?: boolean; active?: boolean; danger?: boolean; disabled?: boolean; style?: CSSProperties
}) {
  const S = size === 'sm' ? 28 : 34
  const I = size === 'sm' ? 15 : 17
  const btn = (
    <button type="button" aria-label={label} aria-pressed={active != null ? !!active : undefined} disabled={disabled} onClick={disabled ? undefined : onClick}
      className={'gx-iconbtn gx-hit' + (framed && !disabled ? ' mo-lift mo-stamp' : '')}
      style={{ position: 'relative', display: 'inline-grid', placeItems: 'center', width: S, height: S, padding: 0, flex: 'none', borderRadius: size === 'sm' ? 8 : 10,
        border: framed ? '2px solid var(--ink)' : '2px solid transparent', background: active ? 'var(--sel)' : framed ? 'var(--card)' : 'transparent',
        boxShadow: framed ? 'var(--el-1)' : 'none', color: danger ? 'var(--coral-tx)' : 'var(--tx)', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, ...style }}>
      <Icon name={icon} size={I} />
    </button>
  )
  return <Tooltip label={label}>{btn}</Tooltip>
}

export function Brand({ compact }: { compact?: boolean }) {
  return (
    <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 13, textDecoration: 'none', color: 'var(--tx)', flex: 'none' }} aria-label="GRYD Live">
      <img src={brandMark} alt="" style={{ width: 30, height: 30, borderRadius: 7, boxShadow: 'var(--el-1)' }} />
      {!compact && <span className="gl-wordmark" style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, letterSpacing: 'var(--track-display)', whiteSpace: 'nowrap' }}>GRYD <span style={{ color: 'var(--brand-tx)' }}>Live</span></span>}
    </a>
  )
}

export function TopBar({ tag, title, right }: { tag?: ReactNode; title?: ReactNode; right?: ReactNode }) {
  if (embedded) return null
  return (
    <header className="gl-topbar">
      <Brand />
      {tag}
      {title && <><span className="gl-divider" /><span className="gl-topbar-title">{title}</span></>}
      <div className="gl-topbar-right">{right}</div>
    </header>
  )
}

export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ fontSize: 'var(--type-label)', letterSpacing: 'var(--track-label)', textTransform: 'uppercase', fontWeight: 700, color: 'var(--mut)', ...style }}>{children}</div>
}

export function LiveDot({ on }: { on: boolean }) {
  return <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: on ? 'var(--lime)' : 'var(--coral)', display: 'inline-block' }} />
}

/**
 * The round emoji button in the stage's corner: opens the reactions beside it.
 * Covers only the corner; the tray stays open for a few in a row.
 */
export function StageReact({ onReact, locked, onLocked }: { onReact: (emoji: string) => void; locked?: boolean; onLocked?: () => void }) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement)?.closest?.('[data-react]')) setOpen(false) }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onDown) }
  }, [open])
  return (
    <div data-react style={{ position: 'relative' }}>
      {open && (
        <div role="group" aria-label="Send a reaction" className="gl-react-tray">
          {ROOM_REACTIONS.map(e => (
            <button key={e} type="button" className="gl-emoji-btn" aria-label={`React ${e}`} onClick={() => onReact(e)}>{e}</button>
          ))}
        </div>
      )}
      <button type="button" className="gl-react-btn" aria-label={locked ? 'React (add your name first)' : 'React with an emoji'} aria-expanded={open} title="React"
        onClick={() => { if (locked) { onLocked?.(); return } setOpen(o => !o) }}>
        <span aria-hidden style={{ fontSize: 22, lineHeight: 1 }}>😀</span>
        <span aria-hidden className="gl-react-plus">+</span>
      </button>
    </div>
  )
}

/** Floating room reactions. Reduced motion: a quiet running tally instead. */
export function Floaters({ floaters }: { floaters: Floater[] }) {
  const reduced = prefersReducedMotion()
  const [tally, setTally] = useState<Record<string, number>>({})
  useEffect(() => {
    if (!reduced || !floaters.length) return
    setTally(t => {
      const n = { ...t }
      for (const f of floaters) n[f.emoji] = (n[f.emoji] ?? 0) + 1
      return n
    })
    const id = window.setTimeout(() => setTally({}), 4000)
    return () => window.clearTimeout(id)
  }, [floaters, reduced])
  if (reduced) {
    const entries = Object.entries(tally)
    if (!entries.length) return null
    return (
      <div aria-live="polite" style={{ position: 'absolute', right: 16, bottom: 16, display: 'flex', gap: 6, zIndex: 3, pointerEvents: 'none' }}>
        {entries.map(([e, n]) => <span key={e} data-floater="tally"><Tag>{e} ×{n}</Tag></span>)}
      </div>
    )
  }
  return (
    <div aria-hidden style={{ position: 'absolute', right: 14, bottom: 'calc(var(--gl-corner-bottom, 14px) + 50px)', width: 150, height: '62%', pointerEvents: 'none', zIndex: 3 }}>
      {floaters.map(f => (
        <span key={f.id} data-floater="float" style={{ position: 'absolute', left: `${f.x}%`, bottom: 0, fontSize: 30, transform: 'translateX(-50%)', animation: `gl-float 3.2s ease-out ${f.delay}ms forwards`, opacity: 0 }}>{f.emoji}</span>
      ))}
    </div>
  )
}

export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), ms); return () => window.clearInterval(id) }, [ms])
  return now
}
